import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Users, Home, Wrench, Building, Settings } from "lucide-react";

const roles = [
  { id: "tenant", label: "Tenant", icon: Home, path: "/tenant", color: "text-blue-600" },
  { id: "contractor", label: "Contractor", icon: Wrench, path: "/contractor", color: "text-green-600" },
  { id: "landlord", label: "Landlord", icon: Building, path: "/landlord", color: "text-purple-600" },
  { id: "admin", label: "Admin", icon: Settings, path: "/admin", color: "text-red-600" },
];

export const RoleSwitcher = () => {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(() => {
    const stored = localStorage.getItem("user");
    return stored ? JSON.parse(stored) : null;
  });

  const switchRole = (newRole) => {
    if (!currentUser) return;
    
    // Update user role in localStorage
    const updatedUser = { ...currentUser, role: newRole };
    localStorage.setItem("user", JSON.stringify(updatedUser));
    setCurrentUser(updatedUser);
    
    // Navigate to the appropriate dashboard
    const roleConfig = roles.find(r => r.id === newRole);
    if (roleConfig) {
      navigate(roleConfig.path);
      window.location.reload(); // Refresh to apply role change
    }
  };

  const currentRole = roles.find(r => r.id === currentUser?.role);
  const CurrentIcon = currentRole?.icon || Users;

  return (
    <div className="fixed bottom-4 right-4 z-50">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            className="rounded-full h-14 w-14 shadow-lg bg-slate-900 hover:bg-slate-800"
            data-testid="role-switcher-btn"
          >
            <Users className="h-6 w-6" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48 mb-2">
          <DropdownMenuLabel className="flex items-center gap-2">
            <CurrentIcon className={`h-4 w-4 ${currentRole?.color}`} />
            Currently: {currentRole?.label || "Unknown"}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-xs text-muted-foreground">
            Switch to:
          </DropdownMenuLabel>
          {roles.map((role) => {
            const Icon = role.icon;
            const isActive = currentUser?.role === role.id;
            return (
              <DropdownMenuItem
                key={role.id}
                onClick={() => switchRole(role.id)}
                className={`cursor-pointer ${isActive ? "bg-slate-100" : ""}`}
                data-testid={`switch-to-${role.id}`}
              >
                <Icon className={`h-4 w-4 mr-2 ${role.color}`} />
                {role.label}
                {isActive && <span className="ml-auto text-xs text-muted-foreground">✓</span>}
              </DropdownMenuItem>
            );
          })}
          <DropdownMenuSeparator />
          <div className="px-2 py-1.5 text-xs text-muted-foreground">
            🧪 Test Mode Only
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
